import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { CompanyLogoUploadSection } from "@/components/companies/CompanyLogoUploadSection";
import { CompanyPrimaryColorSection } from "@/components/companies/CompanyPrimaryColorSection";
import { TimezoneSelect } from "@/components/companies/TimezoneSelect";
import { CurrencySelect } from "@/components/companies/CurrencySelect";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { createCompany, fetchOwnerRoleTemplatePermissionsPreview } from "@/lib/admin/companiesStore";
import {
  companyFormErrorMessage,
  companyFormToPayload,
  emptyCompanyForm,
  type CompanyFormState,
} from "@/lib/admin/companyForm";
import { listManagedPermissions, syncManagedPermissionsFromApi } from "@/lib/admin/managedPermissionsStore";
import { can, getStoredAuthUser, isSuperAdmin } from "@/lib/authUser";
import { ArrowLeft, Building2, Eye, EyeOff, ListTree, Loader2, Save, Shield } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState, type ComponentType } from "react";
import { Link, useNavigate } from "react-router-dom";

/** Spatie role name; must match `CompanyBootstrapService::DEFAULT_OWNER_ROLE_NAME` (backend). */
const COMPANY_OWNER_SPATIE_ROLE = "Company Owner";

/** Country dropdown is Pakistan-only for now (matches Add/Edit Employee pattern). */
const PAKISTAN_ONLY = "Pakistan" as const;
const COUNTRY_SELECT_NONE = "__country_none__";
const PROVINCE_SELECT_NONE = "__province_none__";
const PAKISTAN_PROVINCES = [
  "Punjab",
  "Sindh",
  "Khyber Pakhtunkhwa",
  "Balochistan",
  "Islamabad Capital Territory",
  "Gilgit-Baltistan",
  "Azad Jammu and Kashmir",
] as const;

function PakistanCountrySelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const trimmed = value.trim();
  const orphan = trimmed && trimmed !== PAKISTAN_ONLY ? trimmed : null;
  const selectValue = orphan ?? (!trimmed ? COUNTRY_SELECT_NONE : PAKISTAN_ONLY);

  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground">Country</Label>
      <Select
        value={selectValue}
        onValueChange={(v) => {
          if (v === COUNTRY_SELECT_NONE) onChange("");
          else onChange(v);
        }}
      >
        <SelectTrigger className="glass-input h-10">
          <SelectValue placeholder="Country" />
        </SelectTrigger>
        <SelectContent position="popper">
          <SelectItem value={COUNTRY_SELECT_NONE}>Select…</SelectItem>
          {orphan ? (
            <SelectItem value={orphan}>
              {orphan} <span className="text-muted-foreground">(not in list)</span>
            </SelectItem>
          ) : null}
          <SelectItem value={PAKISTAN_ONLY}>{PAKISTAN_ONLY}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function PakistanProvinceSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const trimmed = value.trim();
  const provinces = PAKISTAN_PROVINCES as readonly string[];
  const inList = Boolean(trimmed && provinces.includes(trimmed));
  const orphan = trimmed && !inList ? trimmed : null;
  const selectValue = orphan ?? (inList ? trimmed : PROVINCE_SELECT_NONE);

  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground">State</Label>
      <Select value={selectValue} onValueChange={(v) => onChange(v === PROVINCE_SELECT_NONE ? "" : v)}>
        <SelectTrigger className="glass-input h-10">
          <SelectValue placeholder="Select state" />
        </SelectTrigger>
        <SelectContent position="popper">
          <SelectItem value={PROVINCE_SELECT_NONE}>Select…</SelectItem>
          {orphan ? (
            <SelectItem value={orphan}>
              {orphan} <span className="text-muted-foreground">(not in list)</span>
            </SelectItem>
          ) : null}
          {PAKISTAN_PROVINCES.map((p) => (
            <SelectItem key={p} value={p}>
              {p}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

const SectionHeader = ({
  title,
  subtitle,
  icon: Icon,
}: {
  title: string;
  subtitle?: string;
  icon: ComponentType<{ className?: string }>;
}) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
      <Icon className="w-4 h-4 text-primary" />
    </div>
    <div>
      <h3 className="font-display font-semibold text-foreground text-sm">{title}</h3>
      {subtitle ? <p className="text-xs text-muted-foreground">{subtitle}</p> : null}
    </div>
  </div>
);

const AddCompany = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canCreate = can(user, "company_can_create");
  const [form, setForm] = useState<CompanyFormState>(emptyCompanyForm);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Only used on this screen: initial company-owner login.
  const [createForm, setCreateForm] = useState(() => ({
    name: "",
    password: "",
    confirmPassword: "",
  }));
  const [showInitialUserPassword, setShowInitialUserPassword] = useState(false);
  const [showInitialUserConfirmPassword, setShowInitialUserConfirmPassword] = useState(false);
  const [rolePermsLoading, setRolePermsLoading] = useState(false);
  const [ownerPreviewNames, setOwnerPreviewNames] = useState<string[]>([]);
  const [ownerPreviewSource, setOwnerPreviewSource] = useState<string>("");

  const groupedOwnerRolePermissions = useMemo(() => {
    if (ownerPreviewNames.length === 0) return [];
    const byName = new Map(listManagedPermissions().map((p) => [p.name, p]));
    type Row = { name: string; title: string; category: string };
    const rows: Row[] = ownerPreviewNames.map((name) => {
      const rec = byName.get(name);
      return {
        name,
        title: rec?.title?.trim() ? rec.title : name,
        category: rec?.categoryName?.trim() || "Uncategorized",
      };
    });
    const byCat = new Map<string, Row[]>();
    for (const row of rows) {
      if (!byCat.has(row.category)) byCat.set(row.category, []);
      byCat.get(row.category)!.push(row);
    }
    return Array.from(byCat.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([category, items]) => ({
        category,
        items: [...items].sort((a, b) => a.title.localeCompare(b.title)),
      }));
  }, [ownerPreviewNames]);

  useEffect(() => {
    let cancelled = false;
    setRolePermsLoading(true);
    setOwnerPreviewNames([]);
    setOwnerPreviewSource("");
    void (async () => {
      try {
        // Access preview must reflect the seeded global template row only:
        // roles.company_id NULL + roles.is_template = 1.
        const [preview] = await Promise.all([
          fetchOwnerRoleTemplatePermissionsPreview(null, { globalTemplate: true }),
          // Ensures preview titles/categories render reliably (not just raw names).
          syncManagedPermissionsFromApi(),
        ]);
        if (!cancelled) {
          setOwnerPreviewNames(preview.permissionNames);
          setOwnerPreviewSource(preview.source);
        }
      } catch (e) {
        console.error("[AddCompany] Owner role template preview failed", e);
        if (!cancelled) {
          setOwnerPreviewNames([]);
          setOwnerPreviewSource("");
        }
      } finally {
        if (!cancelled) setRolePermsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  const handleCancel = () => navigate("/companies");

  const resetAll = () => {
    setForm(emptyCompanyForm());
    setLogoFile(null);
    setCreateForm({ name: "", password: "", confirmPassword: "" });
    setShowInitialUserPassword(false);
    setShowInitialUserConfirmPassword(false);
  };

  const save = async () => {
    if (isSaving || !canCreate) return;
    if (!form.name.trim()) {
      toast.error("Company name is required.");
      return;
    }
    if (!createForm.name.trim()) {
      toast.error("Contact person is required.");
      return;
    }
    if (!form.email.trim()) {
      toast.error("Company email is required.");
      return;
    }
    if (!createForm.password.trim()) {
      toast.error("Password is required.");
      return;
    }
    if (createForm.password !== createForm.confirmPassword) {
      toast.error("Password and confirm password do not match.");
      return;
    }

    const fullName = createForm.name.trim().replace(/\s+/g, " ");
    const parts = fullName.split(" ");
    if (parts.length < 2) {
      toast.error("Please enter the full name (first and last name) for the contact person.");
      return;
    }
    const firstName = parts[0];
    const lastName = parts.slice(1).join(" ");

    setIsSaving(true);
    try {
      const useTenantTemplate = !isSuperAdmin(user) && user?.company_id != null && user.company_id > 0;
      const r = await createCompany(companyFormToPayload(form), {
        logoFile,
        ...(useTenantTemplate ? { ownerRoleTemplateCompanyId: String(user.company_id) } : {}),
        initialUser: {
          firstName,
          lastName,
          phone: form.phone,
          email: form.email,
          password: createForm.password,
        },
      });
      toast.success(r.message);

      navigate("/companies");
    } catch (e) {
      console.error("[AddCompany] Save failed", e);
      toast.error(companyFormErrorMessage(e));
    } finally {
      setIsSaving(false);
    }
  };

  if (!canCreate) {
    return (
      <PermissionDenied
        title="Cannot add companies"
        message="You don’t have permission to create companies. Ask an administrator to grant company_can_create."
      />
    );
  }

  return (
    <Layout title="Add Company">
      <div className="w-full max-w-none space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5"
              onClick={handleCancel}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-2xl font-display font-bold text-foreground">Add Company</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Logo and company profile, plus access preview for the default Company Owner role. Layout matches{" "}
                <Link to="/employees/add" className="text-primary font-medium hover:underline">
                  Add Employee
                </Link>
                .
              </p>
            </div>
          </div>
          <Button type="button" variant="glass" onClick={handleCancel}>
            Cancel
          </Button>
        </div>

        <CompanyLogoUploadSection logoFile={logoFile} onLogoFileChange={setLogoFile} />

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                <Building2 className="w-4 h-4 text-primary" />
              </div>
              <div className="min-w-0">
                <h3 className="font-display font-semibold text-foreground text-sm">Company profile</h3>
                <p className="text-xs text-muted-foreground">Name, domain, and contact details.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 sm:ml-auto">
              <span
                className={form.status === "active" ? "text-xs text-muted-foreground" : "text-xs font-medium text-foreground"}
              >
                Inactive
              </span>
              <Switch
                id="add-company-status"
                checked={form.status === "active"}
                onCheckedChange={(on) => setForm((f) => ({ ...f, status: on ? "active" : "inactive" }))}
                aria-label="Company active"
              />
              <span
                className={form.status === "active" ? "text-xs font-medium text-foreground" : "text-xs text-muted-foreground"}
              >
                Active
              </span>
            </div>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 py-2">
              <div className="rounded-xl border border-border/60 bg-muted/15 p-4 space-y-4">
                <p className="text-xs font-medium italic text-muted-foreground">Company Identity</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2 sm:col-span-1">
                    <Label className="text-xs text-muted-foreground">Company Name *</Label>
                    <Input
                      className="glass-input h-10"
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Company name"
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-1">
                    <Label className="text-xs text-muted-foreground">Domain</Label>
                    <Input
                      className="glass-input h-10"
                      value={form.domain}
                      onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
                      placeholder="example.com"
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-1">
                    <Label className="text-xs text-muted-foreground">Email</Label>
                    <Input
                      className="glass-input h-10"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      type="email"
                      autoComplete="off"
                      placeholder="company@email.com"
                    />
                  </div>
                </div>
              </div>

              <CompanyPrimaryColorSection
                value={form.primaryColor}
                onChange={(c) => setForm((f) => ({ ...f, primaryColor: c }))}
              />

              <div className="rounded-xl border border-border/60 bg-muted/15 p-4 space-y-4">
                <p className="text-xs font-medium italic text-muted-foreground">Location Details</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <PakistanCountrySelect value={form.country} onChange={(v) => setForm((f) => ({ ...f, country: v }))} />
                  <PakistanProvinceSelect value={form.region} onChange={(v) => setForm((f) => ({ ...f, region: v }))} />
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">City</Label>
                    <Input
                      className="glass-input h-10"
                      value={form.city}
                      onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                      placeholder="City"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Address</Label>
                  <Textarea
                    className="glass-input min-h-[80px] resize-y"
                    value={form.address}
                    onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                    placeholder="Street / area / details"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <TimezoneSelect value={form.timezone} onChange={(v) => setForm((f) => ({ ...f, timezone: v }))} />
                  <CurrencySelect value={form.currencyId} onChange={(v) => setForm((f) => ({ ...f, currencyId: v }))} />
                </div>
              </div>

              <div className="rounded-xl border border-border/60 bg-muted/15 p-4 space-y-4">
                <p className="text-xs font-medium italic text-muted-foreground">Contact Person (who manages the account)</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">
                      Contact Person (Full Name) <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      className="glass-input h-10"
                      value={createForm.name}
                      onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                      autoComplete="name"
                      placeholder="Full name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Contact Number</Label>
                    <Input
                      className="glass-input h-10"
                      value={form.phone}
                      onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                      placeholder="Phone"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-border/60 bg-muted/15 p-4 space-y-4">
                <p className="text-xs font-medium italic text-muted-foreground">Security / Login Setup</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2 max-w-md">
                    <Label htmlFor="add-company-initial-password" className="text-xs text-muted-foreground">
                      Password <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="add-company-initial-password"
                        className="glass-input h-10 pr-10"
                        type={showInitialUserPassword ? "text" : "password"}
                        value={createForm.password}
                        onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowInitialUserPassword((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground hover:text-foreground transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        aria-label={showInitialUserPassword ? "Hide password" : "Show password"}
                      >
                        {showInitialUserPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2 max-w-md">
                    <Label htmlFor="add-company-initial-confirm-password" className="text-xs text-muted-foreground">
                      Confirm Password <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="add-company-initial-confirm-password"
                        className="glass-input h-10 pr-10"
                        type={showInitialUserConfirmPassword ? "text" : "password"}
                        value={createForm.confirmPassword}
                        onChange={(e) => setCreateForm((f) => ({ ...f, confirmPassword: e.target.value }))}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowInitialUserConfirmPassword((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground hover:text-foreground transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        aria-label={showInitialUserConfirmPassword ? "Hide password" : "Show password"}
                      >
                        {showInitialUserConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.03 }}
          className="glass-card p-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
            <SectionHeader
              title="Access preview"
              subtitle="This is what will be synced onto the Company Owner role for the new tenant."
              icon={Shield}
            />
          </div>

          <div className="space-y-4">
              <div className="rounded-xl border border-border/50 bg-muted/10 p-4 space-y-4">
                <div className="space-y-2">
                  {rolePermsLoading ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground py-6 justify-center border border-border/40 rounded-lg bg-background/50">
                      <Loader2 className="w-4 h-4 animate-spin shrink-0" aria-hidden />
                      Loading role template…
                    </div>
                  ) : ownerPreviewNames.length === 0 ? (
                    <p className="text-xs text-muted-foreground border border-border/40 rounded-lg px-3 py-4 bg-background/50">
                      {ownerPreviewSource === "all_permissions"
                        ? "The server will assign every web permission, but the catalog is empty or could not be loaded for labels."
                        : "This template has no permissions, or the preview could not be loaded."}
                    </p>
                  ) : (
                    <ScrollArea className="h-[min(40vh,320px)] rounded-lg border border-border/50 bg-background/60 pr-3">
                      <div className="space-y-4 p-3">
                        <div className="flex items-center gap-2 pb-2 border-b border-border/40">
                          <Checkbox checked={true} disabled />
                          <p className="text-xs text-muted-foreground">Select all</p>
                        </div>
                        {groupedOwnerRolePermissions.map((g) => (
                          <div key={g.category} className="space-y-1.5">
                            <p className="text-[11px] font-semibold text-muted-foreground sticky top-0 bg-background/95 backdrop-blur-sm py-1 z-[1] border-b border-border/30">
                              {g.category}
                            </p>
                            <div className="space-y-1">
                              {g.items.map((p) => (
                                <label key={p.name} className="flex items-start gap-2 rounded-md px-1 py-1 hover:bg-muted/30">
                                  <Checkbox checked={true} disabled className="mt-0.5" />
                                  <span className="min-w-0">
                                    <span className="block text-xs text-foreground leading-snug">{p.title}</span>
                                    <span className="block text-[10px] text-muted-foreground font-mono break-all">{p.name}</span>
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </div>
              </div>
            </div>
        </motion.div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t border-border/40">
          <Button type="button" variant="glass" onClick={resetAll} disabled={isSaving}>
            Reset form
          </Button>
          <Button type="button" variant="premium" onClick={() => void save()} disabled={isSaving} className="gap-2">
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save company
              </>
            )}
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default AddCompany;
